import { v4 as uuidv4 } from "uuid";
import { ReportData } from "../store/types";
import { msgReport } from "../api/report";
import { useMessageStore } from "../store";
import { EnvRegion, getEnvRegion } from "../utils/env";

export enum ReportScene {
  CREATE_WEBSOCKET = "CREATE_WEBSOCKET",
  // 页面展示时
  PAGE_SHOW = "PAGE_SHOW",
  // 页面隐藏
  PAGE_HIDE = "PAGE_HIDE",
  // 展示隐私协议
  PRIVATE_SHOW = "PRIVATE_SHOW",
  // 出现历史记录
  // HISTORY_SHOW = "HISTORY_SHOW",
  // 出现问题类型
  QUESTION_LIST_SHOW = "QUESTION_LIST_SHOW",
  // 点击问题类型
  QUESTION_LIST_CLICK = "QUESTION_LIST_CLICK",
  // 开始排队
  LINE_UP = "LINE_UP",
  // 接收到人工欢迎语
  AGENT_WELCOME = "AGENT_WELCOME",
  // 接收到人工消息
  AGENT_MSG_RECEIVE = "AGENT_MSG_RECEIVE",
  // 出现满意度评价
  SATISFY_COMMEND = "SATISFY_COMMEND",
  // 结束会话
  END_CHAT = "END_CHAT",
  // SOCKET ONCLOSE
  SOCKET_ONCLOSE = "SOCKET_ONCLOSE",
  // SOCKET ONERROR
  SOCKET_ONERROR = "SOCKET_ONERROR",
  // SOCKET 触发了重连
  SOCKET_CONNECT = "SOCKET_CONNECT",
  // 网络变化(避免收集，以防侵犯隐私)
  // NETWORK_CHANGE = "NETWORK_CHANGE",
  // 网络中断
  NETWORK_OFFLINE = "NETWORK_OFFLINE",
  // 心跳超时
  HEART_TIMEOUT = "HEART_TIMEOUT",
  // 接口异常
  API_ERROR = "API_ERROR",
  // 标记已读
  MARK_AS_READ = "MARK_AS_READ",
  // 前端渲染错误
  RENDER_ERROR = "RENDER_ERROR",
  // 补充用户发送消息
  USER_MESSAGE_SEND = "USER_MESSAGE_SEND",
  // 开始上传文件
  UPLOAD_FILE = "UPLOAD_FILE",
  // 点击了重新发起会话
  RE_CHAT_CLICK = "RE_CHAT_CLICK",
  // 取消排队
  CANCEL_LINE_UP = "CANCEL_LINE_UP",
  // 主动点击结束会话
  EXIT_CHAT = "EXIT_CHAT",
  // 重试成功
  API_RETRY_SUCCESS = "API_RETRY_SUCCESS",
  // ACK确认
  ACK_CONFIRM = "ACK_CONFIRM",
  // 复制异常
  COPY_ERROR = "COPY_ERROR",
}

class DataReporter {
  private static instance: DataReporter | null = null;
  private pool: ReportData[] = []; // 数据存储池
  private readonly maxBatchSize: number = 2; // 批量上报阈值
  private traceId: string; // 每次刷新页面生成的唯一traceId

  private constructor() {
    this.initEventListeners();
    this.traceId = uuidv4();
  }

  // 获取单例实例
  public static getInstance(): DataReporter {
    if (!DataReporter.instance) {
      DataReporter.instance = new DataReporter();
    }
    return DataReporter.instance;
  }

  // 初始化事件监听
  private initEventListeners(): void {
    if (typeof window !== "undefined") {
      // 页面关闭/刷新事件
      window.addEventListener("beforeunload", () => this.flush());
      window.addEventListener("unload", () => this.flush());

      window.addEventListener("offline", () => {
        this.report({
          scene: ReportScene.NETWORK_OFFLINE,
        });
        this.flush();
      });
    }
  }

  // 添加数据到上报池
  public report(data: ReportData): void {
    const { conversationId } = useMessageStore.getState();
    const reportData: ReportData = {
      ...data,
      t: Date.now(),
      traceId: this.traceId,
      conversationId,
    };

    this.pool.push(reportData);
    if (this.pool.length >= this.maxBatchSize) {
      this.flush();
    }
  }

  // 执行上报并清空池
  public flush(): void {
    if (this.pool.length === 0) return;

    const dataToSend = [...this.pool];
    this.pool = []; // 清空池

    const reportUrl = process.env.NEXT_PUBLIC_DATA_REPORT_URL as string;
    if (!reportUrl) {
      return;
    }

    // 使用 Beacon API 确保在页面关闭时可靠发送
    try {
      const blob = new Blob([JSON.stringify(dataToSend)]);

      const success = navigator.sendBeacon(reportUrl, blob);

      if (!success) {
        console.warn("sendBeacon failed to queue the request");
      }
    } catch (error) {
      msgReport(dataToSend, reportUrl);
      console.warn("Failed to send logs with beacon:", error);
    }
  }
}

export const reporterInstance = DataReporter.getInstance();

// 上报场景
export const reportScene = (
  scene: ReportScene,
  extra?: string,
  orNotReport?: boolean,
) => {
  if (orNotReport && getEnvRegion() === EnvRegion.OR_PROD) {
    // 海外不上报
    return;
  }
  reporterInstance.report({
    scene,
    extra,
  });
  if (scene === ReportScene.API_ERROR) {
    reporterInstance.flush();
  }
};
